import { useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { assetToast } from '@xdocs/i18n';
import type { DocumentThemeSlug, DocumentVersion } from '@xdocs/schemas';
import { Badge, Button, EmptyState, Skeleton, cn, useI18n, useToast } from '@xdocs/ui';
import { JobProgressCard } from '../../features/jobs/index.js';
import { DOCUMENT_THEME_SLUGS, DOCUMENT_THEME_VISUALS, ManuscriptBodyHtml } from '../../features/reader/index.js';
import { useDocument } from '../documents/index.js';
import { useWs } from '../../ws/WsProvider.js';
import { ThemeCard } from './ThemeCard.js';
import { useStyleApply } from './useStyleApply.js';
import { useStyleT } from './useStyleT.js';
import styles from './StylePage.module.css';

const SAMPLE_CHAPTER_TITLE = 'The Memory Palace';
const SAMPLE_CHAPTER_BODY =
  'For two and a half thousand years, people who needed to remember vast quantities of information have relied on a single, strange technique: they built imaginary buildings in their minds and walked through them.';

/**
 * `/style/:docId` (F65) — the Theme gallery + live preview + Apply &
 * re-render. Presentation-only: no moderation gate, immutable versions
 * (Apply produces a new `restyle`-origin version rather than mutating one),
 * a SEPARATE axis from the app theme and the F66 reader theme.
 */
export function StylePage(): ReactElement {
  const { docId } = useParams<{ docId: string }>();
  const documentId = docId ?? '';
  const navigate = useNavigate();
  const { t } = useStyleT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { wsClient, connectionState } = useWs();

  const { document, isLoading, isError, error } = useDocument(documentId);
  const { apply, isApplying, error: applyError, jobId } = useStyleApply(documentId);

  const [selectedSlug, setSelectedSlug] = useState<DocumentThemeSlug>(DOCUMENT_THEME_SLUGS[0] as DocumentThemeSlug);
  const [appliedSlug, setAppliedSlug] = useState<DocumentThemeSlug | null>(null);

  const versionEnabled = connectionState === 'connected' && Boolean(document?.currentVersionId);
  const versionQuery = useQuery<DocumentVersion>({
    queryKey: ['document', documentId, 'version', document?.currentVersionId],
    queryFn: () => requestTyped(wsClient, 'version.get', { documentId, versionId: document?.currentVersionId as string }),
    enabled: versionEnabled,
  });
  const firstChapter = versionQuery.data?.contentJsonb.chapters.slice().sort((a, b) => a.order - b.order)[0];

  if (!docId) {
    return <EmptyState kind="error" title={t('style:notFound.title')} />;
  }

  if (isLoading) {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Skeleton shape="text-line" width="30%" />
        <Skeleton shape="card" count={4} />
      </div>
    );
  }

  if (isError) {
    if (error?.code === 'NOT_FOUND') {
      return (
        <EmptyState kind="error" title={t('style:notFound.title')} action={{ label: t('style:notFound.cta'), onClick: () => void navigate('/documents') }} />
      );
    }
    if (error?.code === 'FORBIDDEN') {
      return (
        <EmptyState kind="error" title={t('style:forbidden.title')} action={{ label: t('style:forbidden.cta'), onClick: () => void navigate('/documents') }} />
      );
    }
    return <EmptyState kind="error" title={t('style:error.title')} action={{ label: t('style:error.retry'), onClick: () => window.location.reload() }} />;
  }

  if (!document) {
    return <EmptyState kind="error" title={t('style:notFound.title')} />;
  }

  const selectedVisual = DOCUMENT_THEME_VISUALS[selectedSlug];

  async function handleApply(): Promise<void> {
    if (!document || !document.currentVersionId) return;
    try {
      await apply(document.currentVersionId, selectedSlug);
      setAppliedSlug(selectedSlug);
      pushToast(assetToast(locale, 'success', t('style:toast.applied.action'), document.title));
    } catch {
      // `applyError` (from `useStyleApply`) already drives the inline error
      // state below — E19 destructive/mutation errors render adjacent to
      // their trigger, never only a toast.
    }
  }

  const previewVars: CSSProperties = {
    '--style-preview-bg': selectedVisual.pageBackground,
    '--style-preview-text': selectedVisual.bodyTextColor,
    '--style-preview-accent': selectedVisual.accent,
    '--style-preview-font': selectedVisual.bodyFontKind === 'serif' ? 'var(--font-reader-serif)' : 'var(--font-sans)',
  } as CSSProperties;

  return (
    <div className={cn(styles['page'])} data-testid="style-page">
      <div className={cn(styles['gallery'])}>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ChevronLeft size={14} aria-hidden="true" />}
          onClick={() => void navigate(-1)}
        >
          {t('style:back')}
        </Button>
        <div className={cn(styles['eyebrow'])}>
          {t('style:eyebrow')} · {document.title}
        </div>
        <h1 className={cn(styles['heading'])}>{t('style:title')}</h1>
        <p className={cn(styles['description'])}>{t('style:description')}</p>

        <div className={cn(styles['grid'])}>
          {DOCUMENT_THEME_SLUGS.map((slug) => (
            <ThemeCard
              key={slug}
              slug={slug}
              visual={DOCUMENT_THEME_VISUALS[slug]}
              selected={slug === selectedSlug}
              isCurrent={slug === appliedSlug}
              onSelect={setSelectedSlug}
            />
          ))}
        </div>
      </div>

      <aside className={cn(styles['rail'])} style={previewVars} data-testid="style-preview">
        <div className={cn(styles['railLabel'])}>{t('style:preview.title', { themeName: t(`style:theme.${selectedSlug}.name`) })}</div>

        <div className={cn(styles['previewCard'])}>
          <div className={cn(styles['previewCover'])}>
            <span>{DOCUMENT_THEME_VISUALS[selectedSlug].coverStyle}</span>
          </div>
          <div className={cn(styles['previewBody'])}>
            <div className={cn(styles['previewEyebrow'])}>{t('style:preview.chapterOne')}</div>
            <div className={cn(styles['previewChapterTitle'])}>{firstChapter?.title ?? SAMPLE_CHAPTER_TITLE}</div>
            <ManuscriptBodyHtml html={firstChapter?.body.slice(0, 280) ?? SAMPLE_CHAPTER_BODY} className={cn(styles['previewParagraph'])} />
          </div>
        </div>

        <p className={cn(styles['intendedFor'])}>{t('style:preview.intendedFor', { intended: t(`style:theme.${selectedSlug}.intended`) })}</p>

        {applyError?.code === 'TIER_REQUIRED' && selectedVisual.pro ? (
          <div className={cn(styles['upsell'])} role="status">
            <Badge tone="pro">{t('style:badge.pro')}</Badge>
            <p className={cn(styles['upsellText'])}>
              {t('style:upsell.description', { themeName: t(`style:theme.${selectedSlug}.name`) })}
            </p>
            <Button variant="primary" size="sm" onClick={() => void navigate('/settings/billing')} className={cn(styles['applyButton'])}>
              {t('style:upsell.upgrade')}
            </Button>
          </div>
        ) : (
          <>
            {applyError && (
              <p role="alert" className={cn(styles['applyError'])}>
                {t('style:toast.applyFailed')}
              </p>
            )}

            <Button
              variant="primary"
              state={isApplying ? 'loading' : appliedSlug === selectedSlug && jobId ? 'success' : 'idle'}
              disabled={!document.currentVersionId}
              {...(!document.currentVersionId ? { disabledReason: t('style:error.title') } : {})}
              onClick={() => void handleApply()}
              className={cn(styles['applyButton'])}
            >
              {isApplying
                ? t('style:preview.applying')
                : appliedSlug === selectedSlug && jobId
                  ? t('style:preview.applied')
                  : t('style:preview.apply')}
            </Button>
          </>
        )}

        {jobId && appliedSlug === selectedSlug && (
          <div className={cn(styles['jobSection'])}>
            <p className={cn(styles['jobLabel'])}>{t('style:job.title')}</p>
            <JobProgressCard jobId={jobId} />
          </div>
        )}
      </aside>
    </div>
  );
}
